import { parseAsInteger, useQueryState } from 'nuqs'
import { useCallback, useState } from 'react'
import type { FilterOperator, FilterState, SortDirection, SortState } from 'types/data-grid'

import { parseFilters, parseSort } from './parsers'

// Stable empty reference so `filters` doesn't produce a new object every
// render when no filter is active. Without this, every downstream
// useMemo/useCallback that depends on `filters` would re-run every render.
const EMPTY_FILTERS: Record<string, FilterState> = {}

export interface DataGridParams {
  sort: SortState | null
  filters: Record<string, FilterState>
  page: number
  toggleSort: (column: string) => void
  /** Set the sort directly (the phone "Sort by" picker); `null` clears it. */
  setSort: (value: SortState | null) => void
  updateFilter: (column: string, operator: FilterOperator, value: string) => void
  clearFilter: (column: string) => void
  setPage: (value: number) => void
}

/**
 * State management for a DataGrid (or anything shaped like one): a current
 * sort, a map of per-column filters, and a page number. When `storageKey`
 * is provided the state is persisted to the URL via nuqs — namespaced as
 * `<storageKey>.s` / `<storageKey>.f` / `<storageKey>.p`. Omit `storageKey`
 * for ephemeral in-memory state.
 *
 * Reusable on its own if you ever need DataGrid-style state without the
 * matching table rendering (e.g. a custom picker with its own filter UI).
 */
export function useDataGridParams(storageKey?: string): DataGridParams {
  const prefix = storageKey ? `${storageKey}.` : ''

  // nuqs hooks must always be called; when there's no storageKey we write
  // to unnamespaced keys that nobody reads. Kept in sync with the local
  // fallbacks so consumers see the same values either way.
  const [urlSort, setUrlSort] = useQueryState(`${prefix}s`, parseSort)
  const [urlFilters, setUrlFilters] = useQueryState(`${prefix}f`, parseFilters)
  const [urlPage, setUrlPage] = useQueryState(`${prefix}p`, parseAsInteger)

  const [localSort, setLocalSort] = useState<SortState | null>(null)
  const [localFilters, setLocalFilters] = useState<Record<string, FilterState>>({})
  const [localPage, setLocalPage] = useState(0)

  const sort: SortState | null = storageKey ? urlSort : localSort
  const filters: Record<string, FilterState> = storageKey ? (urlFilters ?? EMPTY_FILTERS) : localFilters
  const page = storageKey ? (urlPage ?? 0) : localPage

  const writeSort = useCallback(
    (value: SortState | null) => {
      if (storageKey) {
        void setUrlSort(value)
      } else {
        setLocalSort(value)
      }
    },
    [storageKey, setUrlSort],
  )

  const writeFilters = useCallback(
    (value: Record<string, FilterState>) => {
      if (storageKey) {
        // nuqs drops the param from the URL when we pass null, so empty
        // filter objects disappear instead of lingering as `contacts.f=`.
        void setUrlFilters(Object.keys(value).length === 0 ? null : value)
      } else {
        setLocalFilters(value)
      }
    },
    [storageKey, setUrlFilters],
  )

  const writePage = useCallback(
    (value: number) => {
      if (storageKey) {
        void setUrlPage(value === 0 ? null : value)
      } else {
        setLocalPage(value)
      }
    },
    [storageKey, setUrlPage],
  )

  const toggleSort = useCallback(
    (column: string) => {
      let direction: SortDirection = 'asc'
      if (sort?.column === column && sort.direction === 'asc') {
        direction = 'desc'
      } else if (sort?.column === column && sort.direction === 'desc') {
        writeSort(null)
        writePage(0)
        return
      }
      writeSort({ column, direction })
      writePage(0)
    },
    [sort, writeSort, writePage],
  )

  const setSort = useCallback(
    (value: SortState | null) => {
      writeSort(value)
      writePage(0)
    },
    [writeSort, writePage],
  )

  const updateFilter = useCallback(
    (column: string, operator: FilterOperator, value: string) => {
      writeFilters({ ...filters, [column]: { operator, value } })
      writePage(0)
    },
    [filters, writeFilters, writePage],
  )

  const clearFilter = useCallback(
    (column: string) => {
      const next = { ...filters }
      delete next[column]
      writeFilters(next)
      writePage(0)
    },
    [filters, writeFilters, writePage],
  )

  const setPage = useCallback(
    (value: number) => {
      writePage(value)
    },
    [writePage],
  )

  return { sort, filters, page, toggleSort, setSort, updateFilter, clearFilter, setPage }
}
